import React, { useState, useEffect, useMemo } from "react";
import { IoArrowBack } from "react-icons/io5";
import { Plus, Trash2, Pause, Play, Check, X, Pencil, Sparkles } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { showStatusToast } from "../../components/ui/Toast";

// Clé virtuelle (jamais envoyée telle quelle en base : capability=null côté
// backend) désignant les instructions générales, toujours injectées quel
// que soit le sujet de la demande — voir evaAgentCore.js#buildSystemPrompt.
const GENERAL_KEY = "__general__";

// Instructions ajoutées par un admin, chacune injectée dans le system
// prompt d'Eva quand elle est active (voir backend-myalfa/controllers/eva/
// evaAgentCore.js#getCustomInstructions) - permet d'ajuster son
// comportement (ton, priorités, rappels...) sans toucher au code. Chaque
// instruction est scopée soit à "Général" (toujours injectée), soit à une
// capability précise (LM, todos, détails client...) - dans ce cas elle
// n'est injectée QUE dans le bloc de cette capability, pour ne jamais
// gonfler le prompt (donc les tokens envoyés à Claude) avec des
// instructions hors-sujet pour la demande en cours.
function EvaInstructions({ onCancel }) {
  const [instructions, setInstructions] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedKey, setSelectedKey] = useState(GENERAL_KEY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [newContent, setNewContent] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [instructionsRes, capabilitiesRes] = await Promise.all([
        httpClient.get("https://backend-myalfa.vercel.app/api/eva/instructions"),
        httpClient.get("https://backend-myalfa.vercel.app/api/eva/instructions/capabilities"),
      ]);
      setInstructions(instructionsRes.data || []);
      setCapabilities(capabilitiesRes.data || []);
    } catch (err) {
      console.error("Erreur chargement instructions Eva :", err);
      setLoadError(
        "Impossible de charger les instructions. Réessayez, ou contactez le support si l'erreur persiste."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Colonne de gauche : "Général" toujours épinglé en premier, puis une
  // entrée par capability connue côté backend (déjà dans l'ordre où Eva les
  // annonce dans son system prompt).
  const sidebarItems = useMemo(
    () => [
      {
        key: GENERAL_KEY,
        label: "Général",
        isGeneral: true,
        description:
          "Règles générales appliquées à TOUTES les réponses d'Eva, quel que soit le sujet de la demande (ton, priorités, rappels...) — contrairement aux autres capabilities ci-dessous, qui ne s'appliquent qu'à leur sujet précis.",
      },
      ...capabilities,
    ],
    [capabilities]
  );

  const instructionsForSelected = useMemo(
    () =>
      instructions.filter((i) =>
        selectedKey === GENERAL_KEY ? !i.capability : i.capability === selectedKey
      ),
    [instructions, selectedKey]
  );

  const countFor = (key) =>
    instructions.filter((i) => (key === GENERAL_KEY ? !i.capability : i.capability === key)).length;

  const selectedItem = sidebarItems.find((c) => c.key === selectedKey);
  const selectedLabel = selectedItem?.label || "";
  const selectedDescription = selectedItem?.description || "";

  const handleAdd = async () => {
    const content = newContent.trim();
    if (!content) return;
    setAdding(true);
    const toast = showStatusToast({
      status: "loading",
      titre: "Ajout en cours...",
      message: "Nouvelle instruction pour Eva.",
    });

    // Optimiste : l'instruction apparaît dans la liste tout de suite (id
    // temporaire, marquée `_pending`), avant même la réponse serveur — le
    // toast "en cours" informe qu'elle n'est pas encore confirmée. Si le
    // serveur échoue, on la retire et on restitue le texte tapé.
    const tempId = `temp-${Date.now()}`;
    const capability = selectedKey === GENERAL_KEY ? null : selectedKey;
    setInstructions((prev) => [
      ...prev,
      { id: tempId, content, active: true, capability, _pending: true },
    ]);
    setNewContent("");

    try {
      const { data } = await httpClient.post(
        "https://backend-myalfa.vercel.app/api/eva/instructions",
        { content, capability }
      );
      setInstructions((prev) => prev.map((i) => (i.id === tempId ? data : i)));
      toast.update({
        status: "success",
        titre: "Ajoutée",
        message: "Eva applique cette instruction dès le prochain message.",
      });
    } catch (err) {
      console.error("Erreur ajout instruction Eva :", err);
      setInstructions((prev) => prev.filter((i) => i.id !== tempId));
      setNewContent(content);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "L'ajout de l'instruction a échoué.",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleToggleActive = async (instruction) => {
    setBusyId(instruction.id);
    const nextActive = !instruction.active;
    const toast = showStatusToast({
      status: "loading",
      titre: nextActive ? "Réactivation..." : "Mise en pause...",
      message: "Mise à jour de l'instruction.",
    });
    // Optimiste : le badge Active/En pause bascule tout de suite.
    setInstructions((prev) =>
      prev.map((i) => (i.id === instruction.id ? { ...i, active: nextActive } : i))
    );
    try {
      const { data } = await httpClient.patch(
        `https://backend-myalfa.vercel.app/api/eva/instructions/${instruction.id}`,
        { active: nextActive }
      );
      setInstructions((prev) => prev.map((i) => (i.id === data.id ? data : i)));
      toast.update({
        status: "success",
        titre: nextActive ? "Réactivée" : "Mise en pause",
        message: nextActive
          ? "Eva applique de nouveau cette instruction."
          : "Eva n'applique plus cette instruction pour l'instant.",
      });
    } catch (err) {
      console.error("Erreur mise en pause/reprise instruction Eva :", err);
      setInstructions((prev) =>
        prev.map((i) => (i.id === instruction.id ? { ...i, active: instruction.active } : i))
      );
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "Impossible de changer l'état de cette instruction.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (instruction) => {
    setEditingId(instruction.id);
    setEditingContent(instruction.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingContent("");
  };

  const handleSaveEdit = async (id) => {
    const content = editingContent.trim();
    if (!content) return;
    const previous = instructions.find((i) => i.id === id);
    setBusyId(id);
    const toast = showStatusToast({
      status: "loading",
      titre: "Modification en cours...",
      message: "Mise à jour de l'instruction.",
    });
    // Optimiste : le nouveau texte s'affiche tout de suite, l'édition se
    // referme immédiatement — pas d'attente visible pour l'utilisateur.
    setInstructions((prev) => prev.map((i) => (i.id === id ? { ...i, content } : i)));
    cancelEdit();
    try {
      const { data } = await httpClient.patch(
        `https://backend-myalfa.vercel.app/api/eva/instructions/${id}`,
        { content }
      );
      setInstructions((prev) => prev.map((i) => (i.id === data.id ? data : i)));
      toast.update({
        status: "success",
        titre: "Modifiée",
        message: "L'instruction a bien été mise à jour.",
      });
    } catch (err) {
      console.error("Erreur modification instruction Eva :", err);
      // Rollback : on remet l'ancien texte et on rouvre l'édition avec la
      // tentative de l'utilisateur, pour qu'il ne perde rien et puisse réessayer.
      setInstructions((prev) => prev.map((i) => (i.id === id ? previous : i)));
      setEditingId(id);
      setEditingContent(content);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "La modification de l'instruction a échoué.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    const previous = instructions.find((i) => i.id === id);
    const previousIndex = instructions.findIndex((i) => i.id === id);
    setBusyId(id);
    const toast = showStatusToast({
      status: "loading",
      titre: "Suppression en cours...",
      message: "Suppression de l'instruction.",
    });
    // Optimiste : disparaît de la liste tout de suite.
    setInstructions((prev) => prev.filter((i) => i.id !== id));
    try {
      await httpClient.delete(`https://backend-myalfa.vercel.app/api/eva/instructions/${id}`);
      toast.update({
        status: "success",
        titre: "Supprimée",
        message: "L'instruction ne sera plus appliquée par Eva.",
      });
    } catch (err) {
      console.error("Erreur suppression instruction Eva :", err);
      // Rollback : on la remet à sa place d'origine dans la liste.
      setInstructions((prev) => {
        const next = [...prev];
        next.splice(previousIndex, 0, previous);
        return next;
      });
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "La suppression de l'instruction a échoué.",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-[#840040] transition-colors flex-shrink-0"
        >
          <IoArrowBack size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Instructions Eva</h1>
        <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">
          {instructions.length}
        </span>
      </div>
      <p className="text-sm text-gray-400 mb-6 ml-8">
        Instructions ajoutées au comportement d'Eva (l'assistante IA), en plus de ses règles
        habituelles — ton, priorités, rappels... Une instruction "Général" s'applique à toutes ses
        réponses ; une instruction rattachée à une fonctionnalité précise (à gauche) ne s'applique
        qu'à celle-ci, pour ne jamais surcharger Eva d'informations hors-sujet. Ne permet pas de lui
        donner accès à une nouvelle donnée, seulement d'ajuster comment elle répond avec ce qu'elle
        sait déjà faire.
      </p>

      {loadError && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
          <span>{loadError}</span>
          <button
            onClick={load}
            className="flex-shrink-0 text-xs font-semibold text-red-700 hover:text-red-900 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : loadError ? null : (
        <div className="flex flex-col md:flex-row gap-4 items-start">
          {/* Colonne de gauche : capabilities (skills) d'Eva */}
          <div className="w-full md:w-64 flex-shrink-0 bg-white border border-gray-100 rounded-2xl p-2">
            {sidebarItems.map((item) => {
              const isSelected = item.key === selectedKey;
              const count = countFor(item.key);
              if (item.isGeneral) {
                return (
                  <button
                    key={item.key}
                    onClick={() => setSelectedKey(item.key)}
                    className={`w-full flex items-start gap-2 text-left px-3.5 py-2.5 rounded-xl mb-2 border transition-colors ${
                      isSelected
                        ? "bg-[#840040] border-[#840040] text-white"
                        : "bg-gradient-to-r from-[#840040]/5 to-transparent border-[#840040]/20 text-[#840040] hover:from-[#840040]/10"
                    }`}
                  >
                    <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="flex-1 min-w-0 text-sm font-semibold break-words">{item.label}</span>
                    {count > 0 && (
                      <span
                        className={`flex-shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                          isSelected ? "bg-white/20 text-white" : "bg-[#840040]/10 text-[#840040]"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              }
              return (
                <button
                  key={item.key}
                  onClick={() => setSelectedKey(item.key)}
                  className={`w-full flex items-start gap-2 text-left px-3.5 py-2.5 rounded-xl mb-0.5 transition-colors ${
                    isSelected ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span className="flex-1 min-w-0 text-sm break-words">{item.label}</span>
                  {count > 0 && (
                    <span
                      className={`flex-shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                        isSelected ? "bg-gray-200 text-gray-600" : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Colonne de droite : instructions de la capability sélectionnée */}
          <div className="flex-1 w-full min-w-0">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900">{selectedLabel}</h2>
              {selectedDescription && (
                <p className="text-[15px] text-gray-500 mt-1.5 leading-relaxed max-w-2xl">
                  {selectedDescription}
                </p>
              )}
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-4">
              <div className="flex items-start gap-2">
                <textarea
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  placeholder={
                    selectedKey === GENERAL_KEY
                      ? "Ex : Réponds toujours de façon concise et professionnelle."
                      : `Ex : instruction qui ne s'applique qu'à "${selectedLabel}"...`
                  }
                  rows={2}
                  className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] resize-y"
                />
                <button
                  onClick={handleAdd}
                  disabled={adding || !newContent.trim()}
                  className="flex items-center gap-1.5 text-sm text-white bg-[#5C1A30] hover:bg-[#4a1526] px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              </div>
            </div>

            {instructionsForSelected.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-10">
                Aucune instruction pour {selectedKey === GENERAL_KEY ? "l'instant" : `"${selectedLabel}"`}.
              </p>
            ) : (
              <div className="bg-white border border-gray-100 rounded-2xl divide-y divide-gray-100">
                {instructionsForSelected.map((instruction) => {
                  const isEditing = editingId === instruction.id;
                  const isBusy = busyId === instruction.id || instruction._pending;
                  return (
                    <div
                      key={instruction.id}
                      className={`p-4 flex items-start gap-3 ${instruction._pending ? "opacity-60" : ""}`}
                    >
                      {isEditing ? (
                        <>
                          <textarea
                            autoFocus
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            rows={2}
                            className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] resize-y"
                          />
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleSaveEdit(instruction.id)}
                              disabled={isBusy || !editingContent.trim()}
                              className="p-2 rounded-lg text-white bg-[#5C1A30] hover:bg-[#4a1526] transition-colors disabled:opacity-50"
                              title="Enregistrer"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Annuler"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p
                            className={`flex-1 text-sm whitespace-pre-line ${
                              instruction.active ? "text-gray-700" : "text-gray-400 line-through"
                            }`}
                          >
                            {instruction.content}
                          </p>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span
                              className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                                instruction.active
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {instruction._pending ? "Ajout..." : instruction.active ? "Active" : "En pause"}
                            </span>
                            <button
                              onClick={() => startEdit(instruction)}
                              disabled={isBusy}
                              className="p-2 rounded-lg text-gray-400 hover:text-[#840040] hover:bg-gray-100 transition-colors disabled:opacity-50"
                              title="Modifier"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(instruction)}
                              disabled={isBusy}
                              className="p-2 rounded-lg text-gray-400 hover:text-[#840040] hover:bg-gray-100 transition-colors disabled:opacity-50"
                              title={instruction.active ? "Mettre en pause" : "Réactiver"}
                            >
                              {instruction.active ? (
                                <Pause className="w-3.5 h-3.5" />
                              ) : (
                                <Play className="w-3.5 h-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(instruction.id)}
                              disabled={isBusy}
                              className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
                              title="Supprimer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EvaInstructions;
